/**
 * Decide how a channel should be rendered on the watch route.
 *
 * Detection is CAPABILITY-based: it asks what the channel can actually render,
 * never what `programming_type` calls it. That label is a content-genre tag —
 * a 12-episode season and a single leaf episode both carry "Series" — so routing
 * on it sent leaf episodes into the series flow, which had no playlist to resolve
 * and rendered nothing at all (silent black screen).
 *
 * Because the decision is made per load from the channel's current shape, a leaf
 * episode that later gains a playlist promotes itself to the series flow with no
 * code change, migration, or manual reclassification.
 *
 * @param {Object|null|undefined} channel - Channel object from getChannelBySlug
 * @returns {'series'|'movie'|'unplayable'}
 */
export const detectContentType = channel => {
  if (!channel) return 'unplayable';

  const nonEmpty = value => Array.isArray(value) && value.length > 0;

  // An episode list — even of one — renders as a series so the rail is already
  // in place when more episodes are added.
  if (nonEmpty(channel.playlist)) return 'series';

  // A parent wrapping seasons.
  if (nonEmpty(channel.childchannels) || nonEmpty(channel.child_channels)) return 'series';
  if (channel.channel_type === 'parent') return 'series';

  // No episode list and no children, but something to play: a single video.
  // This is the leaf-episode and plain-movie case.
  if (channel.video && channel.video._id) return 'movie';

  // Nothing to render. Callers must surface an error rather than a blank page.
  return 'unplayable';
};

export default detectContentType;
